import React, { useState, useEffect, useCallback } from "react";
import type { CSSProperties } from "react";
import Typography from "./Typography";
import { Image, Loader2 } from "lucide-react";
import { normalizePublicImageUrl } from "../../utils/imageUrl";

interface ResponsiveImageProps {
  image_uri?: string | null;
  alt?: string;
  maxWidth?: string | number;
  maxHeight?: string | number;
  className?: string;
  containerClassName?: string;
  objectFit?: "cover" | "contain" | "fill" | "scale-down";
  showErrorState?: boolean;
  errorMessage?: string;
  placeholderText?: string;
  onLoad?: () => void;
  onError?: () => void;
}

export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  image_uri,
  alt = "Image",
  maxWidth = "100%",
  maxHeight = "100%",
  className = "",
  containerClassName = "",
  objectFit = "contain",
  showErrorState = true,
  errorMessage = "Failed to load image",
  placeholderText = "No image available",
  onLoad,
  onError,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Reset loading state when image_uri changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
    setNaturalSize(null);
  }, [image_uri]);

  // Check if the image is already cached on mount (avoids loading flash)
  const imgRef = useCallback(
    (node: HTMLImageElement | null) => {
      if (node && node.complete && node.naturalWidth > 0) {
        setNaturalSize({
          width: node.naturalWidth,
          height: node.naturalHeight,
        });
        setImageLoaded(true);
        setImageError(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [image_uri],
  );

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    setNaturalSize({
      width: image.naturalWidth,
      height: image.naturalHeight,
    });
    setImageLoaded(true);
    setImageError(false);
    onLoad?.();
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
    onError?.();
  };

  const getObjectFitClass = () => {
    switch (objectFit) {
      case "cover":
        return "object-cover";
      case "contain":
        return "object-contain";
      case "fill":
        return "object-fill";
      case "scale-down":
        return "object-scale-down";
      default:
        return "object-contain";
    }
  };

  // Show placeholder when no image_uri provided
  if (!image_uri) {
    return (
      <div
        className={`w-full flex items-center justify-center bg-gray-100 ${containerClassName}`}
        style={{ maxWidth, maxHeight, minHeight: "120px" }}
      >
        <div className="text-center p-4">
          {placeholderText && (
            <Typography variant="p" className="text-gray-500">
              {placeholderText}
            </Typography>
          )}
          {/* <Typography variant="small" className="text-gray-400">
            Image not available
          </Typography> */}
        </div>
      </div>
    );
  }

  const normalizedSrc = normalizePublicImageUrl(image_uri);
  const cssLength = (value: string | number) =>
    typeof value === "number" ? `${value}px` : value;
  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio > 0
      ? window.devicePixelRatio
      : 1;
  const constrainForDevice = (
    requested: string | number,
    intrinsicPixels?: number,
  ) => {
    const requestedLength = cssLength(requested);
    if (!intrinsicPixels || intrinsicPixels <= 0) {
      return requestedLength;
    }
    const cssPixels = Math.max(1, Math.floor(intrinsicPixels / dpr));
    return `min(${requestedLength}, ${cssPixels}px)`;
  };
  const constrainedMaxWidth = constrainForDevice(maxWidth, naturalSize?.width);
  const constrainedMaxHeight = constrainForDevice(maxHeight, naturalSize?.height);
  const containerStyle: CSSProperties = {
    maxWidth: constrainedMaxWidth,
    maxHeight: constrainedMaxHeight,
  };
  const placeholderStyle: CSSProperties = {
    height: cssLength(maxHeight),
    minHeight: "120px",
    width: cssLength(maxWidth),
  };
  const imageStyle: CSSProperties = {
    maxWidth: constrainedMaxWidth,
    maxHeight: constrainedMaxHeight,
  };

  return (
    <div
      className={`relative ${containerClassName}`}
      style={containerStyle}
    >
      {/* Loading State */}
      {!imageLoaded && !imageError && normalizedSrc && (
        <div
          className="flex items-center justify-center w-full bg-gray-100 rounded-lg animate-pulse"
          style={placeholderStyle}
        >
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
            <Typography variant="small" className="text-gray-500">
              Loading image...
            </Typography>
          </div>
        </div>
      )}

      {/* Error State */}
      {normalizedSrc && imageError && showErrorState && (
        <div
          className="w-full flex items-center justify-center bg-gray-200"
          style={{ height: constrainedMaxHeight, width: constrainedMaxWidth }}
          data-testid="broken-image"
        >
          <div className="text-center p-4">
            <Typography
              variant="small"
              className="flex items-center gap-2 text-gray-400"
            >
              <Image className="w-8 h-8" />
              Error loading image
            </Typography>
          </div>
        </div>
      )}

      {/* Image */}
      {!imageError && normalizedSrc && (
        <img
          ref={imgRef}
          key={normalizedSrc} // Force re-render when image_uri changes
          src={normalizedSrc}
          alt={alt}
          className={`
            block
            rounded-lg
            ${getObjectFitClass()}
            ${imageLoaded ? "opacity-100" : "opacity-0 !h-0"}
            transition-opacity duration-300
            ${className}
          `}
          style={imageStyle}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
    </div>
  );
};
